import { useEffect, useRef, useState } from 'react';
import type { IUserSettingsSubmittalUdf } from 'app/services/userSettingsApi';

/**
 * The body of ONE user detail pane — `submittal/tmpl/user.defind.row.tmpl`
 * (the file name's missing "e" is legacy's).
 *
 * This is what `loadPdf` injects into the empty `tab-pane` on hover (★ Q1, see
 * `FieldDetail.tsx`), and it is where every U-c write starts:
 *   · the title `<input>`      → §6 W2  `PATCH {"name": …}`
 *   · the contenteditable      → §6 W3  `PATCH {"content": …}`
 *   · the "start from new page" checkbox → §6 W4 / ★ Q5 — sends NOTHING
 *   · the `<input type="file">` → §6 W5  multipart `PATCH`
 *
 * A field is EITHER rich-text OR a PDF cut sheet, decided by `udf_type_value`.
 * A PDF field has no `#content_textarea` at all, which is half of why Q5's
 * checkbox is unreachable there (the other half: the checkbox is rendered
 * `non-clickable`).
 *
 * ★ DUPLICATE IDS, ON PURPOSE. Every filled pane emits `id="content_textarea"`,
 * `id="pdf_file"`, `id="<field id>"` on both the checkbox and the upload label.
 * jQuery's delegated selectors match them all (`$(el).is('#content_textarea')`
 * is per-element), so the duplication is load-bearing for legacy's own CSS and
 * is reproduced rather than tidied.
 *
 * ★ Q21 — the "3-65 characters" gate has no teeth. `checkField` toggles
 * `.invalid-field` on the input (real, reproduced below) and
 * `button[name="done_btn"].non-clickable` (dead — the Done/Cancel block at the
 * bottom of the template is inside an HTML comment, which is also why
 * `'click #cancel'` is bound to nothing). A 1-character name still saves,
 * because the blur-save never consults the flag.
 */

interface UserFieldPaneProps {
  field: IUserSettingsSubmittalUdf;
  /**
   * The iframe's `src`. `loadPdf` (`:615-617`) sets it from `data-placeholder`,
   * i.e. `pdf_file` or the empty string.
   * ★ Q15 — when the field has no PDF this really is `""`, the browser resolves
   * it to the current page, and the console logs
   * `Refused to display … X-Frame-Options: deny`. That console line is legacy
   * behaviour and is deliberately not guarded away.
   */
  pdfSrc: string;
  /** `errorMSG` (`:379-392`) — when set, the `<iframe>` is REMOVED and replaced
   * by `<span id="span_error">`. */
  pdfError: string | null;
  /** `blur .user_defined_field` → `updateUserDefinedField`, then
   * `blur .content_title` → `saveUserDefinedFieldData`. One callback: the two
   * legacy handlers always run back-to-back on the same element, in that order
   * (Backbone binds `events` in declaration order). */
  onTitleBlur: (field: IUserSettingsSubmittalUdf, value: string) => void;
  /** Same pairing for `#content_textarea`; the value is the raw `innerHTML`. */
  onContentBlur: (field: IUserSettingsSubmittalUdf, html: string) => void;
  /** ★ Q5 — `changeNewPage` (`:440-445`) writes `self.data` and stops. */
  onNewPageChange: (checked: boolean) => void;
  /** `change #pdf_file` → `uploadPDF` (`:324-362`). */
  onPdfFileChange: (field: IUserSettingsSubmittalUdf, input: HTMLInputElement) => void;
}

/**
 * `user.defind.row.tmpl:35` puts `name="content"` on a `<div>` — not valid HTML
 * for that element, but `updateUserDefinedField` (`:307-313`) branches on
 * `elem.attr('name')` to decide between `elem.val()` and `elem.html()`, and the
 * attribute is in the rendered DOM. Spread through a cast so TypeScript's
 * element typings do not reject an attribute the browser is perfectly happy to
 * keep.
 */
const CONTENT_NAME_ATTR = { name: 'content' } as unknown as React.HTMLAttributes<HTMLDivElement>;

const UserFieldPane = ({
  field,
  pdfSrc,
  pdfError,
  onTitleBlur,
  onContentBlur,
  onNewPageChange,
  onPdfFileChange,
}: UserFieldPaneProps) => {
  const isPdf = field.udf_type_value === 'pdf';
  /** `checkField` (`:417-426`) — `.invalid-field` under 3 characters. */
  const [invalid, setInvalid] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const firstSrc = useRef(true);
  /**
   * `viewPDF` (`:394-415`) swaps the src inside a `.fadeOut(50) … .fadeIn(150)`
   * pair. jQuery's `.attr()` is synchronous, so the NEW document is already
   * loading while the element fades out and back in — reproduced with the same
   * two durations. Skipped on the first paint, where legacy has no fade either
   * (`loadPdf` sets the attribute directly).
   */
  useEffect(() => {
    if (firstSrc.current) {
      firstSrc.current = false;
      return;
    }
    const el = iframeRef.current;
    if (!el) return;
    el.style.transition = 'opacity 50ms linear';
    el.style.opacity = '0';
    const timer = window.setTimeout(() => {
      el.style.transition = 'opacity 150ms linear';
      el.style.opacity = '1';
    }, 50);
    return () => window.clearTimeout(timer);
  }, [pdfSrc]);

  return (
    <>
      <div className="col-xs-12 content-header">
        <div className="col-xs-7 content-title">
          <input
            type="text"
            name="name"
            className={`content_title textarea-field-title user_defined_field title_user_defined_field_${
              field.id
            }${invalid ? ' invalid-field' : ''}`}
            minLength={3}
            maxLength={65}
            defaultValue={field.name ?? ''}
            data-id={field.id}
            onKeyUp={(e) => setInvalid(e.currentTarget.value.length < 3)}
            onChange={(e) => setInvalid(e.currentTarget.value.length < 3)}
            onBlur={(e) => onTitleBlur(field, e.currentTarget.value)}
          />
        </div>
        <div className="col-xs-5">
          <div className="row">
            {isPdf && (
              <div className="col-xs-5 text-right" style={{ top: '-1px' }}>
                {/* The `<form>` is the FormData source — `uploadPDF` (`:330`)
                    does `new FormData(document.querySelector('#pdf_upload_<id>'))`,
                    which is why the file input must live inside it and carry
                    `name="pdf_file"`. */}
                <form id={`pdf_upload_${field.id}`} encType="multipart/form-data" data-id={field.id}>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="editing-btn udf_pdf_field">
                    <i className="icon-upload-file-black-24dp udf-type-icon" />
                    <input
                      id="pdf_file"
                      type="file"
                      data-id={field.id}
                      name="pdf_file"
                      className="hide"
                      tabIndex={-1}
                      accept="application/pdf"
                      onChange={(e) => onPdfFileChange(field, e.currentTarget)}
                    />
                    <span className="pdf-file-label" id={String(field.id)}>
                      Upload
                    </span>
                  </label>
                </form>
              </div>
            )}
            <div
              className={`d-flex col-xs-6 text-right padding-right-0 ${
                isPdf ? '' : ' col-xs-offset-6 '
              }`}
            >
              {/* ★ Q5 lives on this element. `change` writes `self.data` and
                  issues NO request; the value only reaches the server if a
                  title/content blur happens to PATCH afterwards — and clicking
                  the checkbox blurs the contenteditable FIRST, so the blur-save
                  runs BEFORE the change handler and the value is left behind.
                  For a PDF field the input is `non-clickable` (pointer-events
                  none) and there is no `#content_textarea` to blur at all, so
                  the toggle is unreachable. Mirrored exactly. */}
              <input
                className={`${isPdf ? ' non-clickable ' : ''}new_page_user_defined_field_${
                  field.id
                } input-start-page`}
                type="checkbox"
                defaultChecked={field.checkbox_start_from_new_page}
                name="checkbox_start_from_new_page"
                value="1"
                id={String(field.id)}
                tabIndex={-1}
                onChange={(e) => onNewPageChange(e.currentTarget.checked)}
              />
              <label className="label-start-page">Start from new page</label>
            </div>
          </div>
        </div>
      </div>
      <div className="col-xs-12 content-tooltip">
        <div className="col-xs-6 title-tooltip">
          <span>3-65 characters</span>
        </div>
      </div>
      <div className="col-xs-12 content-text ">
        <div className="col-xs-12">
          {isPdf ? (
            <div id={`pdf_block_${field.id}`} className="pdf-block">
              {pdfError === null ? (
                <iframe
                  ref={iframeRef}
                  title=""
                  /* ★ The template really does interpolate the PDF URL as a CSS
                     CLASS (`class="{{pdf_file}} iframe-css"`). Kept. */
                  className={`${field.pdf_file ?? ''} iframe-css`}
                  id={`view_pdf_${field.id}`}
                  style={{ width: '100%', height: '100%' }}
                  frameBorder="0"
                  data-placeholder={field.pdf_file ?? ''}
                  src={pdfSrc}
                />
              ) : (
                <span id="span_error"> {pdfError}</span>
              )}
            </div>
          ) : (
            /* `{{{content}}}` — raw, unescaped HTML, read back out as
               `elem.html()` on blur. React must never own these children, so
               the body goes in through `dangerouslySetInnerHTML` and is never
               re-supplied: the user's edits live in the DOM, exactly as they do
               under Backbone (`saveUserDefinedFieldData` reads the live
               `innerHTML`, and no re-render follows the save). */
            <div
              className={`edit col-xs-12 content-editable textarea-field col-xs-12 field_user_defined_field_${field.id} user_defined_field`}
              {...CONTENT_NAME_ATTR}
              data-id={field.id}
              contentEditable
              suppressContentEditableWarning
              id="content_textarea"
              dangerouslySetInnerHTML={{ __html: field.content ?? '' }}
              onBlur={(e) => onContentBlur(field, e.currentTarget.innerHTML)}
            />
          )}{' '}
        </div>
      </div>
      {/* `user.defind.row.tmpl:40-45` — the Done/Cancel block is an HTML comment
          in legacy, which is why `'click #cancel'` and `button[name="done_btn"]`
          are dead (§5.2 #9, Q21). Not rendered. */}
    </>
  );
};

export default UserFieldPane;
